import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import React from "react";

import { DoneIcon, FailedIcon, WarningIcon } from "./StepIcons";
import { MY_TZ } from "../../utils/constants";

dayjs.extend(utc);
dayjs.extend(timezone);

function formatUTCtoMYTDateTime(date) {
  return dayjs.utc(date).tz(MY_TZ).format("DD MMM YYYY, h:mm a");
}

function formatUTCtoMYTDate(date) {
  return dayjs.utc(date).tz(MY_TZ).format("DD MMM YYYY");
}

function formatUtcOffset(date) {
  if (typeof date !== "string") {
    return date;
  }

  const formatted = moment(date)
    .utcOffset("+0800")
    .format("DD MMM YYYY");

  return formatted;
}

function getTransactionType(type) {
  let transactionType = "";

  switch (type) {
    case "CorrBuy":
      transactionType = "Adjusted Cash In";
      break;
    case "CorrSell":
      transactionType = "Adjusted Cash Out";
      break;
    case "CorrSWI":
      transactionType = "Adjusted Switch In";
      break;
    case "CorrSWO":
      transactionType = "Adjusted Switch Out";
      break;
    case "Buy":
    case "ManBuy":
      transactionType = "Cash In";
      break;
    case "RSPBuy":
      transactionType = "Auto Cash In";
      break;
    case "Dividend":
      transactionType = "Income Distribution";
      break;
    case "Bonus":
      transactionType = "Bonus Distribution";
      break;
    case "CoolOff":
      transactionType = "Cooling Off";
      break;
    case "Campaign":
      transactionType = "Campaign Return";
      break;
    case "TransferIn":
      transactionType = "Switch In";
      break;
    case "TransferOut":
      transactionType = "Switch Out";
      break;
    case "SwitchIn":
    case "ManSwitchIn":
      transactionType = "Switch In";
      break;
    case "SwitchOut":
    case "ManSwitchOut":
      transactionType = "Switch Out";
      break;
    case "SwitchAll":
    case "ManSwitchAll":
      transactionType = "Switch Out";
      break;
    case "BonusUnits":
      transactionType = "Bonus Units";
      break;
    default:
      transactionType = "Cash out";
      break;
  }
  return transactionType;
}

function getStatus({ status, transactionType, details, planId }) {
  const isCashout =
    transactionType === "Sell" ||
    transactionType === "FullSell" ||
    transactionType === "ManSell" ||
    transactionType === "ManFullSell";

  const isCoolOff = transactionType === "CoolOff";

  const corrections = isCorrectionsExist(details);
  switch (status) {
    case "3":
      //special case for mpRetire, status 3 treat as processing
      return planId === "P11" ? "Processing" : "Submitted";
    case "6":
      return "Submitted";
    case "4":
      return "Processing";
    case "5":
      const corrsellWithoutPayment =
        Array.isArray(corrections) &&
        corrections.every(
          item =>
            item.hasOwnProperty("reversal") && item.hasOwnProperty("correction")
        );

      const reversalWithoutPayment =
        Array.isArray(corrections) &&
        corrections.every(
          item =>
            item.hasOwnProperty("reversal") &&
            !item.hasOwnProperty("correction")
        );

      // Cashout reversal without payment
      if (isCashout) {
        return reversalWithoutPayment
          ? "Reverse Completed"
          : corrsellWithoutPayment
          ? "Completed"
          : "Processing";
      }

      if (isCoolOff) return "Processing";

      return reversalWithoutPayment ? "Reverse Completed" : "Completed";

    case "10":
      // only contain reversal
      return Array.isArray(corrections) &&
        corrections?.some(
          item =>
            item.hasOwnProperty("reversal") &&
            !item.hasOwnProperty("correction")
        )
        ? "Reverse Completed"
        : "Completed";
    case "9":
    case "11":
      if (isCashout && status === "11") return "Pending Payment";

      return "Failed";
    default:
      return "";
  }
}

function decideAmount({
  transactionType,
  details,
  amount,
  fee,
  actualAmount,
  cashOutamtSell,
  status,
  planId,
  amountOnly,
  salesChargeAmount,
}) {
  let result;
  const isSwitch =
    transactionType === "SwitchIn" ||
    transactionType === "SwitchOut" ||
    transactionType === "ManSwitchIn" ||
    transactionType === "ManSwitchOut" ||
    transactionType === "SwitchAll" ||
    transactionType === "ManSwitchAll";

  const isSwitchOut =
    transactionType === "ManSwitchAll" ||
    transactionType === "SwitchAll" ||
    transactionType === "SwitchOut" ||
    transactionType === "ManSwitchOut";

  const isBuy = transactionType === "Buy" || transactionType === "ManBuy";
  const iRSPBuy = transactionType === "RSPBuy";
  const isDividendOrBonusOrCoolOffOrBonusUnit =
    transactionType === "Dividend" ||
    transactionType === "Bonus" ||
    transactionType === "CoolOff" ||
    transactionType === "BonusUnits";
  const isCashout =
    transactionType === "Sell" ||
    transactionType === "ManSell" ||
    transactionType === "ManFullSell";
  const isFullsale = transactionType === "FullSell";
  const isRetire = planId === "P11";

  const corrections = isCorrectionsExist(details);

  if (transactionType === "CorrSWI")
    return (
      actualAmount -
      salesChargeAmount -
      (details?.find(d => d.type === transactionType) || {})?.serviceTaxAmount
    );
  if (transactionType === "CorrSWO") return actualAmount - salesChargeAmount;

  if (isSwitch) {
    // refund
    const isRefundCompleted = getRefundResult(
      details,
      status,
      transactionType === "SwitchIn"
    );
    if (isRefundCompleted) return amount;

    const switchType = details?.find(item => item.type === transactionType);

    if (switchType?.corrections?.length > 0) {
      const lastItem =
        switchType?.corrections[switchType?.corrections?.length - 1];

      const correctionStage = lastItem.hasOwnProperty("correction");
      const propToPickFrom =
        lastItem[correctionStage ? "correction" : "reversal"];

      const mappedAmount = {
        SwitchIn: correctionStage
          ? propToPickFrom?.actualAmt -
            propToPickFrom?.salesChargeAmount -
            propToPickFrom?.serviceTaxAmount
          : propToPickFrom?.actualAmt,
        SwitchOut: propToPickFrom?.actualAmt,
        SwitchAll: propToPickFrom?.actualAmt,
      };

      return mappedAmount[transactionType];
    }

    if (isSwitchOut) return amountOnly;

    return amount;
  }

  // retire cashout specific value
  if ((transactionType === "Sell" || isFullsale) && isRetire) return amountOnly;

  const lastCorrection = corrections[corrections?.length - 1];

  const getBasicTransactionTopAmount = fallbackValue =>
    lastCorrection?.correction
      ? lastCorrection?.correction?.actualAmt -
        lastCorrection?.correction?.feeAmount
      : fallbackValue;

  if (isBuy) {
    result = getBasicTransactionTopAmount(
      (isRetire ? amountOnly : amount) + fee
    );
  } else if (isDividendOrBonusOrCoolOffOrBonusUnit) {
    result = amount;
  } else if (details.length > 0 && details[0].adjustments.length > 0) {
    result = details[0].adjustmentAmount - fee;
  } else if (iRSPBuy) {
    result = amount + fee;
  } else if (isCashout) {
    result = getBasicTransactionTopAmount(cashOutamtSell - fee);
  } else if (isFullsale) {
    result = status !== "10" ? amount - fee : amount;
  } else {
    result = amount;
  }

  return Math.abs(result);
}

function decideTransactionMethod({ transactionType, method }) {
  if (
    (transactionType === "Sell" &&
      (method === "FPX" || method === "BankAccount")) ||
    (method === "FPX" && transactionType !== "ManBuy") ||
    transactionType === "RSPBuy"
  ) {
    return "Online Banking";
  }

  if (method === "TnG") return "Touch 'n Go eWallet";

  if (method === "FPXRetire") return "FPX";

  if (transactionType === "ManBuy") {
    if (method === "PPA Online") return "PPA Online";

    return "Manual Buy";
  }

  return method;
}

const getCompletedRelated = ({ status, isDoneIconActive }) => {
  switch (status) {
    case "9":
      return {
        icon: <FailedIcon active />,
        text: "Failed",
      };
    case "11":
      return {
        icon: <WarningIcon active />,
        text: "Pending Payment",
      };
    default:
      return {
        icon: <DoneIcon active={isDoneIconActive} />,
        text: "Completed",
      };
  }
};

const getRefundText = (type, details) => {
  if (!type) {
    return `Switch Out amount have refunded to`;
  }

  const hasSwitchOutWithAdjustments = details.some(item => {
    if (
      (item.type === "SwitchOut" || item.type === "ManSwitchOut") &&
      item.status === "9"
    ) {
      return true;
    }
    if (
      (item.type === "SwitchAll" || item.type === "ManSwitchAll") &&
      item.status === "9"
    ) {
      return true;
    }
    if (
      (item.type === "SwitchOut" || item.type === "ManSwitchOut") &&
      item.adjustments.length > 0
    ) {
      return true;
    }
    if (
      (item.type === "SwitchAll" || item.type === "ManSwitchAll") &&
      item.adjustments.length > 0
    ) {
      return true;
    }
    return false;
  });

  if (hasSwitchOutWithAdjustments) {
    return `Switch In amount have refunded to`;
  } else {
    return `pending refund to`;
  }
};

const getSuccessText = (status, middleText, planName) => {
  if (status === "9") {
    return `Switch ${middleText} request failed`;
  } else {
    return `Your switch-${middleText.toLowerCase()} amount is on the way to ${planName}.`;
  }
};

const getRefundStatusText = (status, type, details) => {
  let result;
  if ((type === "SwitchOut" || type === "ManSwitchOut") && status === "9") {
    return "Refund Completed";
  }
  if ((type === "SwitchAll" || type === "ManSwitchAll") && status === "9") {
    return "Refund Completed";
  }
  if (type === "SwitchIn") {
    if (status === "9") {
      result = details.some(item => {
        if (
          (item.type === "SwitchOut" || type === "ManSwitchOut") &&
          item.status === "9"
        ) {
          return true;
        }
        if (
          (item.type === "SwitchAll" || item.type === "ManSwitchAll") &&
          item.status === "9"
        ) {
          return true;
        }
        if (
          (item.type === "SwitchOut" || type === "ManSwitchOut") &&
          item.adjustments.length > 0
        ) {
          return true;
        }
        if (
          (item.type === "SwitchAll" || item.type === "ManSwitchAll") &&
          item.adjustments.length > 0
        ) {
          return true;
        }
        return false;
      });
      if (result) {
        return "Refund Completed";
      } else {
        return "Pending Refund";
      }
    } else {
      return "Pending Refund";
    }
  } else {
    return "";
  }
};

const getRefundResult = (details, status, trxtype) => {
  let result;
  if (status === "9" && !trxtype) {
    result = true;
    return result;
  }
  result = details.some(item => {
    if (
      (item.type === "SwitchOut" || item.type === "ManSwitchOut") &&
      item.status === "9"
    ) {
      return true;
    }
    if (
      (item.type === "SwitchAll" || item.type === "ManSwitchAll") &&
      item.status === "9"
    ) {
      return true;
    }
    if (
      (item.type === "SwitchOut" || item.type === "ManSwitchOut") &&
      item.adjustments.length > 0
    ) {
      return true;
    }
    if (
      (item.type === "SwitchAll" || item.type === "ManSwitchAll") &&
      item.adjustments.length > 0
    ) {
      return true;
    }
    return false;
  });

  return result;
};

const isCorrectionsExist = details => {
  return details[0]?.corrections?.length > 0 && details[0]?.corrections;
};

export {
  decideAmount,
  decideTransactionMethod,
  getStatus,
  getTransactionType,
  formatUtcOffset,
  formatUTCtoMYTDateTime,
  formatUTCtoMYTDate,
  getCompletedRelated,
  getRefundText,
  getSuccessText,
  getRefundStatusText,
  getRefundResult,
  isCorrectionsExist,
};
